import { layersAtom } from '@/atoms/layers.atom';
import { selectedLayerIdsAtom } from '@/atoms/layers.atom';
import {
  DEFAULT_STOCKPILE_TOOL_MODE,
  stockpileComputeProgressAtom,
  stockpileToolModeAtom,
  stockpileVisualizationAtom,
  stockpileVisualizationVisibleAtom,
} from '@/atoms/stockpile.atom';
import { pointCloudTargetsAtom } from '@/atoms/viewer.atom';
import { project } from '@/core';
import { execute } from '@/graphql/execute';
import { LayerType } from '@/graphql/graphql';
import {
  computeStockpileVolume,
  estimateCellCount,
  StockpileComputeAbortError,
  type ComputeVolumeResult,
} from '@/lib/stockpile/computeVolume';
import { buildStockpileProperties, parseStockpileProperties } from '@/lib/stockpile/feature';
import { MATERIALS, resolveDensity, type MaterialId } from '@/lib/stockpile/materials';
import { evalBestFitPlane, polygonCentroid2D } from '@/lib/stockpile/pointInPolygon';
import { stockpileSourceOptions } from '@/lib/stockpile/sourceOptions';
import { SourceLayerSelect } from '@/components/analyses/SourceLayerSelect';
import { createFeatureMutation, featureQuery, updateFeatureMutation } from '@/queries/feature.query';
import { createLayerMutation } from '@/queries/layer.query';
import { getProjectQuery } from '@/queries/project.query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface StockpileComputePanelProps {
  projectId: string;
}

const DEFAULT_CELL_SIZE = 0.25;
const MIN_CELL_SIZE = 0.05;
const MAX_CELL_SIZE = 5;
const HARD_CELL_CAP = 1_000_000;
const SOFT_CELL_WARN = 250_000;
const STOCKPILES_LAYER_NAME = 'Stockpiles';

// Single source of truth for the sampling-quality enum — the form, the
// compute algorithm, and the persisted Feature property all share this
// vocabulary. Used to be 'standard' | 'high' on the form and 'standard' |
// 'fine' inside computeStockpileVolume; the rename-then-rename round-trip
// added two error-prone conversions for no real benefit.
type SamplingQuality = 'standard' | 'fine';

interface FormState {
  name: string;
  cellSize: number;
  samplingQuality: SamplingQuality;
  materialId: MaterialId;
  customDensity: number | null;
}

const DEFAULT_FORM: FormState = {
  name: '',
  cellSize: DEFAULT_CELL_SIZE,
  samplingQuality: 'standard',
  materialId: 'none',
  customDensity: null,
};

/**
 * Side-sheet panel that opens once the user has finished picking their
 * stockpile polygon (i.e. `stockpileToolModeAtom.phase === 'closed'`).
 *
 * Responsibilities:
 *   1. Display the polygon summary (vertex count, footprint, planned cell count).
 *   2. Drive the volume computation via `computeStockpileVolume` with progress + cancel.
 *   3. Persist the result as a Feature on the per-project Stockpiles VECTOR layer
 *      (auto-created on first save).
 *   4. CRS guard: refuse on geographic project SRIDs (lat/lon).
 *
 * Why a Sheet rather than a Dialog: the user generally wants to compare the
 * computed numbers against the polygon they just drew, so we side-dock the
 * panel and leave the canvas visible.
 */
export const StockpileComputePanel = ({ projectId }: StockpileComputePanelProps) => {
  const [mode, setMode] = useAtom(stockpileToolModeAtom);
  const [progress, setProgress] = useAtom(stockpileComputeProgressAtom);
  const setSelectedLayerIds = useSetAtom(selectedLayerIdsAtom(projectId));
  const setVisualization = useSetAtom(stockpileVisualizationAtom);
  const [vizVisible, setVizVisible] = useAtom(stockpileVisualizationVisibleAtom);
  const layers = useAtomValue(layersAtom);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const queryClient = useQueryClient();
  const { t } = useTranslation('project');

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [result, setResult] = useState<ComputeVolumeResult | null>(null);
  const [computing, setComputing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Monotonic clamp on the bar percentage. The compute pipeline reports
   * progress on a per-phase denominator: phase 1 (loading) goes 0–60% of the
   * bar at one rate, phase 2 (computing) goes 60–100% at another. Even with
   * the weighted formula the bar can briefly regress on phase boundaries
   * (e.g. when `selectedNodes.length` differs from `toLoad.length`, or when
   * a stray pre-abort onProgress from an earlier compute slips in just as
   * the new one starts), and React's CSS-transition smoothing makes those
   * regressions visible as a "jumping up and down" bar.
   *
   * We clamp by remembering the max percent shown and never going below it
   * within a single compute. The ref is reset every time the panel re-opens
   * for a new polygon (see the open-toggle effect) so the bar starts fresh.
   */
  const monotonicPercentRef = useRef(0);

  const open = mode.enabled && mode.phase === 'closed';

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });
  const projectSrid = projectData?.project?.srid ?? null;
  // Only WGS84 lat/lon is geographic for our purposes. -1 (local point-cloud
  // frame) and null (unset) are treated as projected/metric — volume math works.
  const isLatLonSrid = projectSrid === 4326;

  // ── Edit-mode plumbing ────────────────────────────────────────────────────
  // When the panel is opened via the "edit existing stockpile" path
  // (`mode.editFeatureId !== null`), we fetch that feature so we can pre-fill
  // the form (name, cell size, sampling quality, material, custom density).
  // The polygon vertices are already in `mode.pickedPoints` because
  // `enterStockpileEditModeAtom` sets them from feature.geometry — we don't
  // re-read them here.
  const editFeatureId = mode.editFeatureId;
  const { data: editFeatureData } = useQuery({
    queryKey: ['feature', editFeatureId],
    queryFn: () => execute(featureQuery, { id: editFeatureId! }),
    enabled: !!editFeatureId,
  });

  // Reset local state every time the panel re-opens with a new polygon.
  // For edit mode, `setForm(DEFAULT_FORM)` runs first; the populate-from-feature
  // effect below then overwrites it once the feature query resolves.
  useEffect(() => {
    if (open) {
      setForm(DEFAULT_FORM);
      setResult(null);
      setComputing(false);
      setProgress(null);
      setVisualization(null);
      // Reset the bar's monotonic clamp so the next compute starts back at
      // 0 % (the ref persists across renders, including across polygon
      // sessions, so without this it'd hold the previous compute's
      // high-water mark).
      monotonicPercentRef.current = 0;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      abortRef.current?.abort();
      abortRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Populate the form from the loaded feature when in edit mode.
  // `parseStockpileProperties` handles both the flat shape and the
  // legacy nested shape, so old DB rows still pre-fill correctly.
  useEffect(() => {
    if (!open || !editFeatureId || !editFeatureData?.feature) return;
    const parsed = parseStockpileProperties(editFeatureData.feature.properties);
    if (!parsed) return;
    setForm({
      name: parsed.name,
      cellSize: Number.isFinite(parsed.cellSize) && parsed.cellSize > 0 ? parsed.cellSize : DEFAULT_CELL_SIZE,
      samplingQuality: parsed.samplingQuality,
      materialId: parsed.materialId,
      // Custom density only applies when the material id is 'custom'; for
      // the preset materials the density is fixed in MATERIALS.
      customDensity: parsed.materialId === 'custom' ? parsed.densityKgPerM3 : null,
    });
  }, [open, editFeatureId, editFeatureData?.feature]);

  // Cleanup the viz when the component fully unmounts (defensive — Sheet
  // unmounts on close-via-Escape too, in which case `open` already flipped false).
  useEffect(() => {
    return () => setVisualization(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const polygonXYZ = mode.pickedPoints;
  const polygonXY = useMemo<Array<[number, number]>>(() => polygonXYZ.map((p) => [p[0], p[1]]), [polygonXYZ]);

  // Cell-count preview: live-recomputed when cellSize changes. Cheap (<1 ms for
  // typical polygons at typical cell sizes) — happens inline.
  const estimatedCells = useMemo(() => {
    if (polygonXY.length < 3) return 0;
    return estimateCellCount(polygonXY, form.cellSize);
  }, [polygonXY, form.cellSize]);

  // Pile XY footprint (cell-count × cell²) — shown alongside the cell count
  // so the user has a sanity-check footprint figure before computing.
  const footprintM2 = estimatedCells * form.cellSize * form.cellSize;

  const overSoftCap = estimatedCells > SOFT_CELL_WARN;
  const overHardCap = estimatedCells > HARD_CELL_CAP;

  // D10: the sampled cloud is an explicit pick. Default = the old auto-pick
  // (first visible cloud), already written into `mode.sourceLayerId` by the
  // launcher or by the first vertex pick.
  const sourceOptions = useMemo(
    () =>
      stockpileSourceOptions(
        pcoTargets.map((p) => p.userData?.layerId as string | undefined).filter((id): id is string => !!id),
        layers,
        mode.sourceLayerId,
      ).map((o) => ({ id: o.id, name: o.loaded ? o.name : t('stockpile.sourceNotLoaded', { name: o.name }) })),
    [pcoTargets, layers, mode.sourceLayerId, t],
  );
  const handleSourceChange = (id: string) => {
    if (id === mode.sourceLayerId) return;
    // A result computed on the other cloud is meaningless now.
    abortRef.current?.abort();
    setMode({ ...mode, sourceLayerId: id });
    setResult(null);
    setProgress(null);
    setVisualization(null);
  };
  const sourcePco = mode.sourceLayerId ? pcoTargets.find((p) => p.userData?.layerId === mode.sourceLayerId) : null;

  const density = resolveDensity(form.materialId, form.customDensity);
  const massKg = result && density ? result.volumeM3 * density : null;

  const handleClose = () => {
    abortRef.current?.abort();
    setMode({ ...DEFAULT_STOCKPILE_TOOL_MODE });
    setProgress(null);
    setVisualization(null);
  };

  // Confirmation gate for accidental closes (overlay click, Escape).
  // Drawing a polygon and computing the volume is real user effort —
  // losing it to a stray click is the kind of frustration users report.
  // The explicit Discard / Save buttons stay direct (intentional clicks
  // don't need confirmation); only the *passive* close paths route
  // through the dialog.
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // What's at risk when the panel closes:
  //   - Fresh polygon (`!editFeatureId`) → losing the drawing itself.
  //   - Computed result (`result`) → losing volume / area / etc. since
  //     `Save` hasn't fired (true even in edit mode where the polygon is
  //     persisted but the recomputed values aren't).
  //   - In-flight compute (`computing`) → losing partial work.
  // Edit-mode-without-changes (`editFeatureId && !result && !computing`)
  // is a clean exit — no confirm.
  const hasUnsavedWork = !editFeatureId || result != null || computing;

  const handleSheetOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    if (hasUnsavedWork) {
      setConfirmDiscardOpen(true);
      return; // don't close the sheet — Radix is controlled, our prop wins
    }
    handleClose();
  };

  const handleConfirmDiscard = () => {
    setConfirmDiscardOpen(false);
    handleClose();
  };

  const handleCompute = async () => {
    if (!sourcePco) {
      toast.error(t('stockpile.errorNoSourcePco'));
      return;
    }
    if (overHardCap) {
      toast.error(t('stockpile.errorTooManyCells', { count: HARD_CELL_CAP.toLocaleString() }));
      return;
    }
    if (isLatLonSrid) {
      toast.error(t('stockpile.errorGeographicSrid'));
      return;
    }

    const ac = new AbortController();
    abortRef.current = ac;
    setComputing(true);
    setResult(null);
    setProgress({ phase: 'loading', done: 0, total: 0 });
    // Reset the monotonic-bar clamp so this compute starts at 0 % rather than
    // wherever the previous compute happened to leave the bar.
    monotonicPercentRef.current = 0;

    // Direct call into the camera-independent compute. No CustomEvent / R3F
    // context dance any more — the new algorithm walks `pco.pcoGeometry`
    // directly and reads point buffers, so it doesn't need a renderer or
    // camera. The previous bridge (StockpileComputeHost mounted inside Canvas)
    // is gone.
    try {
      const r = await computeStockpileVolume({
        polygonXY,
        polygonXYZ,
        pco: sourcePco,
        cellSize: form.cellSize,
        // Same enum on both sides now — no conversion.
        samplingQuality: form.samplingQuality,
        captureCells: true,
        signal: ac.signal,
        onProgress: (phase, done, total) => {
          setProgress({ phase, done, total });
        },
      });
      setResult(r);
      setComputing(false);
      setProgress(null);
      if (r.cells) {
        setVisualization({
          cells: r.cells,
          polygonRing: polygonXYZ,
          basePlane: r.basePlane,
          maxHeight: r.maxHeightM,
        });
      }
    } catch (err: any) {
      setComputing(false);
      setProgress(null);
      if (err instanceof StockpileComputeAbortError) {
        toast.info(t('stockpile.computeCanceled'));
      } else {
        toast.error(err?.message ?? t('stockpile.computeFailed'));
      }
    }
  };

  const handleCancelCompute = () => {
    abortRef.current?.abort();
  };

  const ensureStockpilesLayer = async (): Promise<string> => {
    // Look up an existing Stockpiles layer by exact name + type. Layer
    // metadata isn't writable via createLayer, so we use the name as marker.
    // Multi-stockpile-layer scenarios (e.g. user manually renamed one) fall
    // through to "create a new one" — minor UX cost, no functional risk.
    const existing = layers.find(
      (l) => l.type === ('VECTOR' as LayerType) && l.name === STOCKPILES_LAYER_NAME && !l.parentLayerId,
    );
    if (existing) return existing.id;
    const created = await execute(createLayerMutation, {
      input: {
        projectId,
        name: STOCKPILES_LAYER_NAME,
        type: 'VECTOR' as LayerType,
      },
    });
    if (!created?.createLayer?.id) throw new Error(t('stockpile.errorCreateLayer'));
    // Refresh layer list so subsequent calls in this session see the new layer
    // synchronously (without waiting for the next refetch interval).
    await queryClient.invalidateQueries({ queryKey: ['layers'] });
    return created.createLayer.id;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!result) throw new Error('no result');

      // Build the result-side properties payload. The geometry/perimeter is
      // identical between create and update flows (we don't allow editing the
      // polygon vertices in v1), so the difference between the two paths is
      // purely whether we mint a new Feature or update an existing one's
      // properties in place.
      const ring = [...polygonXYZ, polygonXYZ[0]] as Array<[number, number, number]>;
      const geometry = {
        type: 'Polygon' as const,
        crs: { type: 'name' as const, properties: { name: `urn:ogc:def:crs:EPSG::${projectSrid ?? -1}` } },
        coordinates: [ring],
      };

      // Project to WGS84 for the geometryWGS84 column. project() may return
      // undefined if proj4 isn't loaded yet — in that case we omit the WGS84
      // geometry and let the server fall back (mirrors AddSurfaceToolbar).
      // SRID -1 (local point-cloud frame) and null (unset) have no geographic
      // origin, so reprojection is impossible — skip cleanly.
      let geometryWGS84: typeof geometry | null = null;
      if (projectSrid !== null && projectSrid !== -1) {
        try {
          const wgsRing = await Promise.all(ring.map((c) => project(c, `EPSG:${projectSrid}`)));
          if (wgsRing.every((p) => p)) {
            geometryWGS84 = {
              type: 'Polygon' as const,
              crs: { type: 'name' as const, properties: { name: `urn:ogc:def:crs:EPSG::4326` } },
              coordinates: [wgsRing as Array<[number, number, number]>],
            };
          }
        } catch {
          // proj4 missing — non-fatal, server uses geometry as-is.
        }
      }

      // Properties go out flat — name first, then headline metrics, then
      // the long tail of compute parameters and provenance. See
      // `buildStockpileProperties` for the full layout rationale.
      const properties = buildStockpileProperties({
        name: form.name.trim() || t('stockpile.untitled'),
        volumeM3: result.volumeM3,
        massKg,
        areaM2: result.areaM2,
        meanHeightM: result.meanHeightM,
        maxHeightM: result.maxHeightM,
        materialId: form.materialId,
        densityKgPerM3: form.materialId === 'none' ? null : (density ?? null),
        cellSize: form.cellSize,
        samplingQuality: form.samplingQuality,
        sampleCount: result.sampleCount,
        missCount: result.missCount,
        basePlane: result.basePlane,
        sourceLayerId: mode.sourceLayerId ?? '',
        computedAt: Date.now(),
      });

      // ── Edit path ────────────────────────────────────────────────────────
      // Existing Feature → update properties only. The geometry stays where
      // the original create put it; we don't allow polygon edits in v1.
      // (If the user wants a different polygon, they Discard and start fresh.)
      if (mode.editFeatureId) {
        const updated = await execute(updateFeatureMutation, {
          input: { id: mode.editFeatureId, properties },
        });
        if (!updated?.updateFeature) throw new Error(t('stockpile.errorSaveFailed'));
        // Invalidate the parent feature list (so labels re-render with the new
        // volume) and the per-feature query (so a re-open shows fresh values).
        if (updated.updateFeature.layerId) {
          await queryClient.invalidateQueries({ queryKey: ['features', updated.updateFeature.layerId] });
        }
        await queryClient.invalidateQueries({ queryKey: ['feature', mode.editFeatureId] });
        return updated.updateFeature;
      }

      // ── Create path ──────────────────────────────────────────────────────
      const layerId = await ensureStockpilesLayer();
      const input: any = {
        layerId,
        geometry,
        properties,
      };
      if (geometryWGS84) input.geometryWGS84 = geometryWGS84;

      const created = await execute(createFeatureMutation, { input });
      if (!created?.createFeature) throw new Error(t('stockpile.errorSaveFailed'));

      // Make the Stockpiles layer visible if it isn't already, so the new
      // polygon + label appear immediately.
      setSelectedLayerIds((prev) => (prev.includes(layerId) ? prev : [...prev, layerId]));

      // Refresh feature query so GeoJson + SavedStockpiles pick up the new feature.
      await queryClient.invalidateQueries({ queryKey: ['features', layerId] });
      // Also refresh layers in case we just created the parent layer.
      await queryClient.invalidateQueries({ queryKey: ['layers'] });

      return created.createFeature;
    },
    onSuccess: () => {
      toast.success(t('stockpile.savedToast'));
      setMode({ ...DEFAULT_STOCKPILE_TOOL_MODE });
      setProgress(null);
      setVisualization(null);
    },
    onError: (e: any) => {
      toast.error(e?.message ?? t('stockpile.errorSaveFailed'));
    },
  });

  const centroid = useMemo(() => (polygonXY.length >= 3 ? polygonCentroid2D(polygonXY) : null), [polygonXY]);

  return (
    <>
      <Sheet open={open} onOpenChange={handleSheetOpenChange}>
        <SheetContent side='right' className='sm:max-w-md w-full flex flex-col gap-4 overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>
              {/* Different titles for create vs. edit so the user can tell which
                flow they're in at a glance. The edit title also surfaces the
                stockpile name (when set) so they remember which pile they're
                touching. */}
              {editFeatureId
                ? form.name
                  ? t('stockpile.panelTitleEditNamed', { name: form.name })
                  : t('stockpile.panelTitleEdit')
                : t('stockpile.panelTitle')}
            </SheetTitle>
            <SheetDescription>
              {editFeatureId ? t('stockpile.panelDescriptionEdit') : t('stockpile.panelDescription')}
            </SheetDescription>
          </SheetHeader>

          <div className='flex flex-col gap-4 px-4 pb-6'>
            {isLatLonSrid && (
              <div className='rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-200'>
                {t('stockpile.warnGeographicSrid')}
              </div>
            )}

            <SourceLayerSelect
              id='stockpile-source-layer'
              testId='stockpile-source-layer'
              label={t('stockpile.sourceLayer')}
              layers={sourceOptions}
              value={mode.sourceLayerId ?? ''}
              onChange={handleSourceChange}
              disabled={computing}
              help={t('stockpile.sourceLayerHelp')}
              emptyMessage={t('viewerLayers.stockpileNoVisiblePco')}
            />

            {/* Polygon summary */}
            <section className='flex flex-col gap-1 text-sm'>
              <div className='flex justify-between'>
                <span className='text-slate-600 dark:text-stone-400'>{t('stockpile.vertexCount')}</span>
                <span className='font-medium'>{polygonXYZ.length}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-slate-600 dark:text-stone-400'>{t('stockpile.footprint')}</span>
                <span className='font-medium'>{footprintM2.toFixed(1)} m²</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-slate-600 dark:text-stone-400'>{t('stockpile.estimatedCells')}</span>
                <span className={'font-medium ' + (overHardCap ? 'text-red-600' : overSoftCap ? 'text-amber-600' : '')}>
                  {estimatedCells.toLocaleString()}
                </span>
              </div>
            </section>

            {/* Form */}
            <section className='flex flex-col gap-3'>
              <div className='flex flex-col gap-1.5'>
                <Label htmlFor='stockpile-name'>{t('stockpile.fieldName')}</Label>
                <Input
                  id='stockpile-name'
                  value={form.name}
                  placeholder={t('stockpile.fieldNamePlaceholder')}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div className='flex flex-col gap-1.5'>
                <Label htmlFor='stockpile-cell-size'>{t('stockpile.fieldCellSize')}</Label>
                <NumericField
                  id='stockpile-cell-size'
                  step='0.01'
                  min={String(MIN_CELL_SIZE)}
                  max={String(MAX_CELL_SIZE)}
                  value={form.cellSize}
                  onChange={(v) => setForm((f) => ({ ...f, cellSize: v }))}
                />
                <p className='text-xs text-slate-600 dark:text-stone-400'>{t('stockpile.fieldCellSizeHelp')}</p>
              </div>

              <div className='flex flex-col gap-1.5'>
                <Label>{t('stockpile.fieldSamplingQuality')}</Label>
                <div className='flex gap-2'>
                  {(['standard', 'fine'] as SamplingQuality[]).map((q) => (
                    <button
                      key={q}
                      type='button'
                      className={
                        'flex-1 px-3 py-1.5 rounded text-sm border transition-colors ' +
                        (form.samplingQuality === q
                          ? 'bg-slate-800 text-white border-slate-800 dark:bg-white dark:text-stone-700 dark:border-white'
                          : 'bg-white border-slate-200 hover:bg-slate-50 dark:bg-stone-900 dark:border-stone-700 dark:hover:bg-stone-800')
                      }
                      onClick={() => setForm((f) => ({ ...f, samplingQuality: q }))}>
                      {t(`stockpile.samplingQuality.${q}`)}
                    </button>
                  ))}
                </div>
                <p className='text-xs text-slate-600 dark:text-stone-400'>{t('stockpile.fieldSamplingQualityHelp')}</p>
              </div>

              <div className='flex flex-col gap-1.5'>
                <Label htmlFor='stockpile-material'>{t('stockpile.fieldMaterial')}</Label>
                <Select
                  value={form.materialId}
                  onValueChange={(v) => setForm((f) => ({ ...f, materialId: v as MaterialId }))}>
                  <SelectTrigger id='stockpile-material'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MATERIALS.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {/* i18next's typed `t()` rejects template-literal keys; cast through any
                          (matches the same pattern used in BatchList.tsx etc.). The runtime
                          lookup is fine because we list the keys exhaustively under
                          `stockpile.materials.*` in every locale. */}
                        {t(`stockpile.materials.${m.labelKey}` as any)}
                        {m.densityKgPerM3 != null && (
                          <span className='ml-2 text-xs text-slate-500 dark:text-stone-400'>
                            {m.densityKgPerM3} kg/m³
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.materialId === 'custom' && (
                <div className='flex flex-col gap-1.5'>
                  <Label htmlFor='stockpile-density'>{t('stockpile.fieldCustomDensity')}</Label>
                  <Input
                    id='stockpile-density'
                    type='number'
                    step='1'
                    min={1}
                    value={form.customDensity ?? ''}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setForm((f) => ({ ...f, customDensity: Number.isFinite(v) ? v : null }));
                    }}
                    placeholder='e.g. 1500'
                  />
                </div>
              )}
            </section>

            {/* Compute / progress */}
            <section className='flex flex-col gap-2'>
              {!result && !computing && (
                <Button
                  onClick={handleCompute}
                  disabled={!sourcePco || isLatLonSrid || overHardCap || estimatedCells === 0 || polygonXYZ.length < 3}>
                  {t('stockpile.computeButton')}
                </Button>
              )}
              {computing && progress && (
                <div className='flex flex-col gap-2'>
                  {(() => {
                    // Smooth the bar across the two compute phases. Earlier
                    // version used `done / total` per phase, which made the
                    // bar reset to 0 % at the loading→computing transition
                    // (loading runs to 100 % on its own denominator, computing
                    // restarts from 1/N on a different denominator). With the
                    // weighted formula below the typical case is monotonic,
                    // BUT the math can still regress in pathological orderings:
                    //
                    //   - Concurrent loaders finishing out of order, where a
                    //     mid-phase progress event from worker A lands after
                    //     worker B has already pushed a higher count.
                    //   - selectedNodes.length differing from toLoad.length
                    //     so the loading→computing handoff can briefly
                    //     compute a smaller percent than the previous loading
                    //     event reported.
                    //   - Stale onProgress events from an aborted-but-not-yet-
                    //     unwound previous compute slipping in next to events
                    //     from the new compute.
                    //
                    // We clamp via `monotonicPercentRef` so the bar never
                    // visually regresses within a single compute. The ref
                    // resets in `handleCompute` (and the open-toggle effect)
                    // so each fresh compute starts back at 0 %.
                    //
                    // Phase weighting:
                    //   loading  → 0–60 % (network + worker decode dominates
                    //                       cold-cache wall time)
                    //   compute  → 60–100 % (CPU iteration once data is in)
                    // Warm-cache recomputes start at 60 % (loading is a
                    // no-op, contributes 0) — that 60 % "head-start" reads
                    // as honest "we already had what we needed" feedback.
                    const LOADING_FRACTION = 0.6;
                    const phaseRatio = progress.total > 0 ? Math.min(1, progress.done / progress.total) : 0;
                    const rawPercent =
                      progress.phase === 'loading'
                        ? phaseRatio * LOADING_FRACTION * 100
                        : LOADING_FRACTION * 100 + phaseRatio * (1 - LOADING_FRACTION) * 100;
                    const clampedPercent = Math.max(monotonicPercentRef.current, rawPercent);
                    // Update during render is normally a smell (causes loops),
                    // but writing to a ref doesn't trigger a re-render — this
                    // is the standard "remember the last computed value" idiom
                    // and matches the React docs' guidance for derived caches.
                    monotonicPercentRef.current = clampedPercent;
                    return <Progress value={clampedPercent} />;
                  })()}
                  <div className='flex items-center justify-between text-xs text-slate-600 dark:text-stone-400'>
                    <span>
                      {/* Per-phase label so the user knows what's happening
                        right now ("Loading…" vs "Computing…") even though
                        the bar tracks overall progress. */}
                      {progress.phase === 'loading'
                        ? t('stockpile.progressLoading', {
                            done: progress.done.toLocaleString(),
                            total: progress.total.toLocaleString(),
                          })
                        : t('stockpile.progressComputing', {
                            done: progress.done.toLocaleString(),
                            total: progress.total.toLocaleString(),
                          })}
                    </span>
                    <button type='button' className='text-red-600 hover:underline' onClick={handleCancelCompute}>
                      {t('stockpile.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Results */}
            {result && (
              <section className='flex flex-col gap-3 rounded-md border border-slate-200 dark:border-stone-700 p-3'>
                <h3 className='text-sm font-semibold text-slate-800 dark:text-stone-100'>
                  {t('stockpile.resultsHeader')}
                </h3>
                <div className='grid grid-cols-2 gap-3 text-sm'>
                  <ResultRow
                    label={t('stockpile.resultVolume')}
                    value={`${result.volumeM3.toLocaleString(undefined, { maximumFractionDigits: 2 })} m³`}
                    prominent
                  />
                  <ResultRow
                    label={t('stockpile.resultArea')}
                    value={`${result.areaM2.toLocaleString(undefined, { maximumFractionDigits: 1 })} m²`}
                  />
                  <ResultRow label={t('stockpile.resultMeanHeight')} value={`${result.meanHeightM.toFixed(2)} m`} />
                  <ResultRow label={t('stockpile.resultMaxHeight')} value={`${result.maxHeightM.toFixed(2)} m`} />
                  <ResultRow label={t('stockpile.resultSampleCount')} value={result.sampleCount.toLocaleString()} />
                  <ResultRow
                    label={t('stockpile.resultMissCount')}
                    value={`${result.missCount.toLocaleString()} (${
                      result.sampleCount > 0 ? Math.round((result.missCount / result.sampleCount) * 100) : 0
                    }%)`}
                  />
                  {centroid && (
                    <ResultRow
                      label={t('stockpile.resultBaseElevationCenter')}
                      value={`${evalBestFitPlane(result.basePlane, centroid[0], centroid[1]).toFixed(2)} m`}
                    />
                  )}
                  <ResultRow
                    label={t('stockpile.resultDuration')}
                    value={`${(result.durationMs / 1000).toFixed(1)} s`}
                  />
                </div>
                {massKg != null && (
                  <div className='flex items-center justify-between border-t border-slate-200 dark:border-stone-700 pt-2'>
                    <span className='text-sm font-semibold text-slate-700 dark:text-stone-200'>
                      {t('stockpile.resultMass')}
                    </span>
                    <span className='text-base font-bold text-orange-600 dark:text-orange-400'>
                      {massKg >= 1000
                        ? `${(massKg / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`
                        : `${massKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`}
                    </span>
                  </div>
                )}
                {result.sampleCount > 0 && result.missCount / result.sampleCount > 0.3 && (
                  <p className='text-xs text-amber-700 dark:text-amber-300'>{t('stockpile.warnLowCoverage')}</p>
                )}
                {/* Visualization toggle — controls the 3D base-plane disc + height
                  columns drawn by <StockpileVisualization />. The viz atom
                  itself is set on compute success and cleared on cancel /
                  discard / save; this checkbox just toggles render visibility
                  without affecting the cached payload. */}
                <label className='flex items-center gap-2 border-t border-slate-200 dark:border-stone-700 pt-2 text-sm cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={vizVisible}
                    onChange={(e) => setVizVisible(e.target.checked)}
                    className='accent-orange-500'
                  />
                  <span className='font-medium text-slate-700 dark:text-stone-200'>
                    {t('stockpile.showVisualization')}
                  </span>
                </label>
                <p className='text-xs text-slate-500 dark:text-stone-400 -mt-1'>
                  {t('stockpile.showVisualizationHelp')}
                </p>
              </section>
            )}

            {/* Save / Recompute / Discard */}
            <div className='flex gap-2 mt-2'>
              {result && (
                <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className='flex-1'>
                  {saveMutation.isPending
                    ? t('stockpile.saving')
                    : editFeatureId
                      ? t('stockpile.saveEdit')
                      : t('stockpile.save')}
                </Button>
              )}
              {result && !computing && (
                <Button
                  variant='outline'
                  onClick={() => {
                    setResult(null);
                    void handleCompute();
                  }}>
                  {t('stockpile.recompute')}
                </Button>
              )}
              <Button variant='ghost' onClick={handleClose}>
                {t('stockpile.discard')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      {/* Confirmation for accidental closes — overlay clicks and Escape
        route through here when there's unsaved work. The Discard / Save
        buttons inside the sheet are direct (intentional clicks shouldn't
        need a second confirmation). */}
      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('stockpile.confirmDiscardTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {result != null
                ? t('stockpile.confirmDiscardDescriptionWithResult')
                : t('stockpile.confirmDiscardDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('stockpile.confirmDiscardKeep')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              className='bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600/30'>
              {t('stockpile.confirmDiscardConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const ResultRow = ({ label, value, prominent }: { label: string; value: string; prominent?: boolean }) => (
  <div className='flex flex-col'>
    <span className='text-xs text-slate-500 dark:text-stone-400'>{label}</span>
    <span className={prominent ? 'text-lg font-bold text-orange-600 dark:text-orange-400' : 'text-sm font-medium'}>
      {value}
    </span>
  </div>
);

/* -----------------------------------------------------------------------------
 * (Removed) StockpileComputeHost — earlier the compute lived behind a
 * CustomEvent bridge mounted inside the R3F Canvas because `pco.pick` needed
 * the active renderer + camera. The new algorithm walks `pco.pcoGeometry`
 * directly and reads point buffers, so no R3F context is needed and the panel
 * can call `computeStockpileVolume` straight through.
 * --------------------------------------------------------------------------- */
